
import { useState, useEffect } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Upload } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const getPublicUrl = (filePath: string) => {
  const { data } = supabase.storage.from('templates').getPublicUrl(filePath);
  return data.publicUrl;
};

interface EditTemplateDialogProps {
  template: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Función para sanitizar nombres de archivo
function sanitizeFileName(fileName: string): string {
  
  // Remover o reemplazar caracteres especiales
  const sanitized = fileName
    .normalize('NFD') // Descomponer caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Remover diacríticos
    .replace(/[^a-zA-Z0-9.-]/g, '_') // Reemplazar caracteres especiales con _
    .replace(/_+/g, '_') // Reemplazar múltiples _ con uno solo
    .replace(/^_|_$/g, ''); // Remover _ al inicio y final
  
  return sanitized;
}

export function EditTemplateDialog({ template, open, onOpenChange }: EditTemplateDialogProps) {
  const [name, setName] = useState(template.name || "");
  const [activeTab, setActiveTab] = useState("basic");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState(template.ppt_file_path || null);
  const [isUploading, setIsUploading] = useState(false);
  const [canvaTemplateUrl, setCanvaTemplateUrl] = useState(template.canva_template_url || "");
  const [jotformFormId, setJotformFormId] = useState(template.jotform_form_id || "");
  const [jotformFormIdReferral, setJotformFormIdReferral] = useState(template.jotform_form_id_referral || "");
  const queryClient = useQueryClient();
  const { projectClient, currentProject, projectId } = useProject();

  // Nuevo estado para el toggle: true = Canva, false = PPTX
  const [useCanva, setUseCanva] = useState(!!template.canva_template_url);

  // Get configured JotForm forms
  const { data: jotformForms = [] } = useQuery({
    queryKey: ["jotform_form_mappings", currentProject],
    queryFn: async () => {
      if (!currentProject) return [];
      
      const projectId = currentProject === '3m' ? 1 : 2;
      
      const { data, error } = await projectClient
        .from("jotform_form_mappings")
        .select(`
          id, 
          jotform_form_id, 
          form_title,
          is_referral_form,
          parent_form_id,
          parent:parent_form_id (
            id,
            form_title,
            jotform_form_id
          )
        `)
        .eq("project_id", projectId)
        .order("form_title");
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentProject && open,
  });

  useEffect(() => {
    setName(template.name || "");
    setFileUrl(template.ppt_file_path || null);
    setCanvaTemplateUrl(template.canva_template_url || "");
    setJotformFormId(template.jotform_form_id || "");
    setJotformFormIdReferral(template.jotform_form_id_referral || "");
    setUseCanva(!!template.canva_template_url);
  }, [template]);

  const handleUpdate = async () => {
    
    try {
      setIsUploading(true);
      
      let pptFilePath = useCanva ? null : template.ppt_file_path;
      let canvaUrlToSave = useCanva ? canvaTemplateUrl : null;
      
      if (!useCanva && selectedFile) {
        
        // Validar tipo de archivo
        const allowedTypes = [
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ];
        
        if (!allowedTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(ppt|pptx)$/i)) {
          throw new Error("Solo se permiten archivos PPT y PPTX");
        }
        
        // Validar tamaño (máximo 50MB)
        const maxSize = 50 * 1024 * 1024; // 50MB
        if (selectedFile.size > maxSize) {
          throw new Error("El archivo no puede ser mayor a 50MB");
        }
        
        // Sanitizar nombre del archivo
        const sanitizedFileName = sanitizeFileName(selectedFile.name);
        
        // Agregar timestamp para evitar colisiones pero mantener el nombre original sanitizado
        const fileName = `${Date.now()}_${sanitizedFileName}`;
        const filePath = `template_files/${fileName}`;

        // Intentar subir el archivo con manejo de errores mejorado
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('templates')
          .upload(filePath, selectedFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          throw new Error(`Error al subir archivo: ${uploadError.message}`);
        }

        // Si había un archivo anterior Y es diferente al nuevo, elimínalo
        if (template.ppt_file_path && template.ppt_file_path !== filePath) {
          
          // Verificar que el archivo anterior existe antes de intentar eliminarlo
          const { data: existingFiles, error: listError } = await supabase.storage
            .from('templates')
            .list('template_files');

          if (listError) {
            console.warn("⚠️ Error al listar archivos:", listError);
          } else {
            const oldFileName = template.ppt_file_path.split('/').pop();
            const fileExists = existingFiles?.some(file => file.name === oldFileName);
            
            if (fileExists) {
              const { error: deleteError } = await supabase.storage
                .from('templates')
                .remove([template.ppt_file_path]);

              if (deleteError) {
                console.warn("⚠️ Error al eliminar archivo anterior:", deleteError);
                // No lanzar error, solo advertir ya que el nuevo archivo ya se subió
              } else {
                console.log("✅ Archivo anterior eliminado exitosamente");
              }
            } else {
              console.log("⚠️ Archivo anterior no encontrado en storage, continuando...");
            }
          }
        }

        pptFilePath = filePath;
        setFileUrl(filePath);
      }

      // Actualizar registro en base de datos
      const { error: updateError } = await projectClient
        .from("report_templates_v2")
        .update({
          name,
          ppt_file_path: pptFilePath,
          canva_template_url: canvaUrlToSave,
          jotform_form_id: jotformFormId || null,
          jotform_form_id_referral: jotformFormIdReferral || null
        })
        .eq("id", template.id)
        .eq('project_id', projectClient.getProjectId());

      if (updateError) {
        throw updateError;
      }

      toast.success("Plantilla actualizada correctamente");
      queryClient.invalidateQueries({ queryKey: ["templates_v2", currentProject] });
      queryClient.invalidateQueries({ queryKey: ["template_v2", currentProject, template.id] });
      onOpenChange(false);
      setSelectedFile(null);
    } catch (error: any) {
      toast.error(`Error al actualizar plantilla: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Editar plantilla</DialogTitle>
          <DialogDescription>
            Modifica las propiedades de tu plantilla de reporte
          </DialogDescription>
        </DialogHeader>

        {/* Name Input */}
        <div className="space-y-2">
          <Label htmlFor="template-name">Nombre de la Plantilla</Label>
          <Input
            id="template-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Reporte de Liderazgo"
          />
        </div>

        {/* JotForm Form Selector - Main Form */}
        <div className="space-y-2">
          <Label htmlFor="jotform-form">Formulario Principal</Label>
          <Select
            value={jotformFormId || undefined}
            onValueChange={(value) => setJotformFormId(value || "")}
          >
            <SelectTrigger id="jotform-form">
              <SelectValue placeholder="Seleccionar formulario principal (opcional)" />
            </SelectTrigger>
            <SelectContent>
              {jotformForms.filter((f: any) => !f.is_referral_form).map((form: any) => (
                <SelectItem key={form.jotform_form_id} value={form.jotform_form_id}>
                  <div className="flex items-center justify-between w-full">
                    <span>{form.form_title || form.jotform_form_id}</span>
                    <Badge variant="secondary" className="text-xs ml-2">
                      Principal
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground">
            Formulario que contiene las respuestas del usuario principal
          </div>
        </div>

        {/* JotForm Form Selector - Referral Form */}
        <div className="space-y-2">
          <Label htmlFor="jotform-form-referral">Formulario de Referidos (Opcional)</Label>
          <Select
            value={jotformFormIdReferral || "none"}
            onValueChange={(value) => setJotformFormIdReferral(value === "none" ? "" : value)}
          >
            <SelectTrigger id="jotform-form-referral">
              <SelectValue placeholder="Seleccionar formulario de referidos (opcional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-muted-foreground">Sin formulario de referidos</span>
              </SelectItem>
              {jotformForms.filter((f: any) => f.is_referral_form).map((form: any) => (
                <SelectItem key={form.jotform_form_id} value={form.jotform_form_id}>
                  <div className="flex items-center justify-between w-full">
                    <span>{form.form_title || form.jotform_form_id}</span>
                    <Badge variant="outline" className="text-xs ml-2">
                      Referido
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {jotformFormIdReferral && (() => {
            const selectedForm = jotformForms.find((f: any) => f.jotform_form_id === jotformFormIdReferral);
            return selectedForm?.parent && selectedForm.parent.length > 0 ? (
              <div className="text-sm text-muted-foreground mt-2 p-2 bg-muted rounded">
                <strong>Formulario padre:</strong> {selectedForm.parent[0].form_title || selectedForm.parent[0].jotform_form_id}
              </div>
            ) : null;
          })()}
          <div className="text-xs text-muted-foreground">
            Formulario para análisis comparativo con las respuestas de los referidos
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <span className="font-medium">Archivo PPTX</span>
          <Switch checked={useCanva} onCheckedChange={setUseCanva} />
          <span className="font-medium">Canva</span>
        </div>
        {useCanva ? (
          <div className="space-y-2">
            <Label>Plantilla de Canva a usar</Label>
            <Input
              placeholder="https://www.canva.com/design/XXXXXX/template"
              value={canvaTemplateUrl}
              onChange={(e) => setCanvaTemplateUrl(e.target.value)}
              className="mt-1"
              disabled={!useCanva}
            />
            <div className="text-xs text-muted-foreground">
              Pega aquí el enlace de la plantilla base de Canva. Puedes obtenerlo desde Canva → Compartir → Plantilla.
            </div>
            {canvaTemplateUrl && (
              <div className="mt-1">
                <a href={canvaTemplateUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">
                  Ver plantilla de Canva
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Label>Plantilla de PPT a usar</Label>
            <Input
              type="file"
              accept=".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  const file = e.target.files[0];
                  setSelectedFile(file);
                }
              }}
              disabled={isUploading || useCanva}
            />
            <div className="text-xs text-muted-foreground">
              Formatos permitidos: PPT, PPTX. Tamaño máximo: 50MB
            </div>
            {(fileUrl || selectedFile) && (
              <div className="mt-2 text-sm text-green-500">
                {selectedFile ? `Archivo seleccionado: ${selectedFile.name}` : `Archivo actual: ${fileUrl?.split('/').pop()}`}
              </div>
            )}
            {fileUrl && (
              <div className="mt-2 text-xs">
                <a
                  href={getPublicUrl(fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                  download
                >
                  Descargar archivo actual
                </a>
              </div>
            )}
            {fileUrl && (
              <div className="mt-2 text-xs text-muted-foreground">
                Ruta actual: {fileUrl}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end space-x-2 mt-8">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={isUploading || !name.trim() || (useCanva ? !canvaTemplateUrl : (!fileUrl && !selectedFile))}
          >
            {isUploading ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
